import { Check } from "lucide-react";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

interface CheckboxProps extends React.ComponentProps<"input"> {
  variant?: "default" | "warning";
}

export function Checkbox({ children, variant = "default", className, ...props }: CheckboxProps) {
  return (
    <label
      className={twMerge(
        clsx(
          "flex gap-x-2 items-center cursor-pointer has-disabled:cursor-not-allowed",
          "text-neutral-700 dark:text-neutral-300 transition-all",
          className,
        ),
      )}
    >
      <span
        className={clsx(
          "relative grid size-5 shrink-0 place-items-center",
          "rounded-sm inset-ring inset-ring-neutral-400/70 bg-white",
          "dark:inset-ring-neutral-700 dark:bg-neutral-900",
          "has-checked:inset-ring-2 cursor-pointer",
          "has-focus-visible:outline-2 has-focus-visible:outline-offset-2",
          "has-disabled:opacity-50",
          {
            "has-checked:inset-ring-blue-500 has-checked:bg-blue-500 has-focus-visible:outline-blue-500":
              variant === "default",
            "has-checked:inset-ring-amber-600 has-checked:bg-amber-600 has-focus-visible:outline-amber-600":
              variant === "warning",
          },
        )}
      >
        <input
          type="checkbox"
          className="peer absolute inset-0 m-0 appearance-none
                     focus-visible:outline-none cursor-pointer"
          {...props}
        />
        <Check
          strokeWidth={4}
          className="pointer-events-none size-3 dark:text-white opacity-30
                     peer-checked:text-white
                     peer-checked:dark:text-black peer-checked:opacity-100"
        />
      </span>
      {children}
    </label>
  );
}

export function Option({ children, variant = "default", className, ...props }: CheckboxProps) {
  return (
    <Checkbox
      className={twMerge(
        clsx(
          "p-2 rounded-md inset-ring inset-ring-neutral-400/70 dark:inset-ring-neutral-700 w-fit",
          "has-checked:inset-ring-2 shadow-md hover:shadow-lg shadow-black/5 dark:shadow-black/30",
          {
            "has-checked:inset-ring-blue-500 has-checked:text-blue-500": variant === "default",
            "has-checked:inset-ring-amber-600 has-checked:text-amber-600": variant === "warning",
          },
        ),
        className,
      )}
      variant={variant}
      {...props}
    >
      <span className="font-medium text-sm">{children}</span>
    </Checkbox>
  );
}
